/**
 * FilterDropdown Component
 *
 * Base multi-select dropdown component for filter strip.
 * Provides consistent UI and behavior across all filter types.
 *
 * @example
 * // Simple filter (products, channels, journeys)
 * <FilterDropdown
 *   value={selectedProducts}
 *   onChange={setSelectedProducts}
 *   options={productOptions}
 *   icon={<Package className="h-4 w-4" />}
 *   emptyLabel="All Products"
 *   pluralNoun="products"
 *   helperText="Select products (OR logic - matches any)"
 * />
 *
 * @example
 * // Filter with descriptions (dimensions, regulatory flags)
 * <FilterDropdown
 *   value={selectedDimensions}
 *   onChange={setSelectedDimensions}
 *   options={dimensionOptions}  // options have 'description' field
 *   icon={<Layers className="h-4 w-4" />}
 *   emptyLabel="All Dimensions"
 *   pluralNoun="dimensions"
 *   helperText="Select dimensions (OR logic)"
 *   showDescriptions
 * />
 */

import { useState, type ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { Button } from '../../../../ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../ui/popover';
import type { FilterLogic } from '../types/filter.types';

/** Option with optional description */
export interface FilterDropdownOption<T extends string = string> {
  value: T;
  label: string;
  description?: string;
}

export interface FilterDropdownProps<T extends string = string> {
  /** Currently selected values */
  value?: T[];
  /** Callback when selection changes */
  onChange: (value: T[] | undefined) => void;
  /** Available options */
  options: FilterDropdownOption<T>[];
  /** Icon to display in the trigger button */
  icon: ReactNode;
  /** Label when nothing is selected (e.g., "All Products") */
  emptyLabel: string;
  /** Plural noun for count display (e.g., "products" -> "3 products") */
  pluralNoun: string;
  /** Helper text shown at top of dropdown */
  helperText?: string;
  /** Whether to show option descriptions (for options that have them) */
  showDescriptions?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Current logic mode for multi-select */
  logicMode?: FilterLogic;
  /** Callback when logic mode changes */
  onLogicModeChange?: (logic: FilterLogic) => void;
  /** Whether to show the logic toggle (default: true when onLogicModeChange provided) */
  showLogicToggle?: boolean;
}

export function FilterDropdown<T extends string = string>({
  value = [],
  onChange,
  options,
  icon,
  emptyLabel,
  pluralNoun,
  helperText,
  showDescriptions = false,
  disabled = false,
  className,
  logicMode = 'or',
  onLogicModeChange,
  showLogicToggle,
}: FilterDropdownProps<T>) {
  const [open, setOpen] = useState(false);

  const hasSelection = value.length > 0;
  const hasMultipleSelected = value.length > 1;
  // Show toggle when: explicitly enabled, or when callback provided
  // (toggle is disabled when < 2 items selected)
  const shouldShowToggle = showLogicToggle ?? !!onLogicModeChange;

  // Toggle a single value
  const toggleValue = (optionValue: T) => {
    const newValues = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];

    onChange(newValues.length > 0 ? newValues : undefined);
  };

  // Clear all selections
  const clearAll = () => {
    onChange(undefined);
  };

  // Get display label for trigger button
  const getDisplayLabel = () => {
    if (!hasSelection) {
      return emptyLabel;
    }
    if (value.length === 1) {
      const option = options.find((o) => o.value === value[0]);
      return option?.label || value[0];
    }
    return `${value.length} ${pluralNoun}`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'justify-between font-normal',
            hasSelection && 'text-foreground',
            !hasSelection && 'text-muted-foreground',
            className
          )}
        >
          <span className="mr-2">{icon}</span>
          <span className="truncate">{getDisplayLabel()}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        {/* Logic Toggle - shown when onLogicModeChange is provided */}
        {shouldShowToggle && (
          <div className={cn('p-2 border-b', !hasMultipleSelected && 'opacity-50')}>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Match:{!hasMultipleSelected && ' (select 2+)'}
              </span>
              <div className="flex rounded-md border overflow-hidden">
                <button
                  type="button"
                  onClick={() => hasMultipleSelected && onLogicModeChange?.('or')}
                  disabled={!hasMultipleSelected}
                  aria-label="Match any selected value (OR logic)"
                  className={cn(
                    'px-3 py-1 text-xs transition-colors',
                    logicMode === 'or'
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-accent',
                    !hasMultipleSelected && 'cursor-not-allowed'
                  )}
                >
                  Any
                </button>
                <button
                  type="button"
                  onClick={() => hasMultipleSelected && onLogicModeChange?.('and')}
                  disabled={!hasMultipleSelected}
                  aria-label="Match all selected values (AND logic)"
                  className={cn(
                    'px-3 py-1 text-xs transition-colors',
                    logicMode === 'and'
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-accent',
                    !hasMultipleSelected && 'cursor-not-allowed'
                  )}
                >
                  All
                </button>
              </div>
            </div>
          </div>
        )}
        {helperText && (
          <div className="p-2">
            <p className="px-2 py-1 text-xs text-muted-foreground">
              {helperText}
            </p>
          </div>
        )}
        <div className="max-h-[300px] overflow-auto">
          {hasSelection && (
            <button
              type="button"
              onClick={clearAll}
              className="w-full px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent border-b"
            >
              Clear selection
            </button>
          )}
          {options.map((option) => {
            const isSelected = value.includes(option.value);
            const hasDescription = showDescriptions && option.description;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => toggleValue(option.value)}
                className={cn(
                  'flex w-full px-3 py-2 text-sm hover:bg-accent',
                  hasDescription ? 'flex-col items-start' : 'items-center',
                  isSelected && 'bg-accent/50'
                )}
              >
                <div className="flex w-full items-center">
                  <div
                    className={cn(
                      'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                      isSelected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-muted-foreground'
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                  <span className={hasDescription ? 'font-medium' : ''}>
                    {option.label}
                  </span>
                </div>
                {hasDescription && (
                  <p className="mt-0.5 ml-6 text-xs text-muted-foreground text-left">
                    {option.description}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
